import { ContentContainer, PageLayout, PageTitle } from "components/layout";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Loader2 } from "lucide-react";
import React, { useMemo, useState } from "react";
import SelectCoupon from "../components/select-coupon";
import SelectEntryExit from "../components/select-entry-exit";
import SelectFilters from "../components/select-filters";
import StoreFilter from "../components/store-filter";
import { useStoresCatalog } from "../hooks/useStoresCatalog";
import { useCreateExtractSchedulesMutation } from "@/services/ofm-extract";
import { toast } from "components/ui/use-toast";
import Schedulers from "../components/schedulers-list";
import { useOfmFiltersState } from "../ducks/filter/hooks/state";
import { useOfmFiltersActions } from "../ducks/filter/hooks/actions";
import { CodInvoiceModel, InvoiceType } from "../constants/const";

export const Extracao: React.FC = () => {
  const { fromDate, toDate, filters } = useOfmFiltersState();
  const { reset } = useOfmFiltersActions();

  const [formKey, setFormKey] = useState(0);

  const { stores: catalog } = useStoresCatalog();

  const selectedCodes: string[] = Array.isArray(filters?.codes)
    ? (filters.codes as string[])
    : (filters?.code ? [String(filters.code)] : []);

  const codInvoiceModel: CodInvoiceModel.NFCe | CodInvoiceModel.NFe | CodInvoiceModel.Both | undefined =
    (filters?.codInvoiceModel as CodInvoiceModel.NFCe | CodInvoiceModel.NFe | CodInvoiceModel.Both) ?? (filters?.couponType as CodInvoiceModel.NFCe | CodInvoiceModel.NFe | CodInvoiceModel.Both);

  const invoiceType: InvoiceType.ENTRY | InvoiceType.EXIT | undefined =
    (filters?.invoiceType as InvoiceType.ENTRY | InvoiceType.EXIT) ?? (filters?.entryExit as InvoiceType.ENTRY | InvoiceType.EXIT);

  const selectedCnpjs = useMemo(() => {
    const map = new Map(catalog.map(it => [it.code, it.cnpj ?? ""]));
    return selectedCodes.map(code => map.get(code) || "").filter(Boolean);
  }, [catalog, selectedCodes]);

  const [createExtractSchedules, { isLoading }] = useCreateExtractSchedulesMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Valida o formulário e, se OK, abre a modal de confirmação (o processo de
  // recálculo/extração OFM pode demorar) antes de disparar o agendamento.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!fromDate || !toDate) {
      toast({ variant: "destructive", description: "Selecone um intervalo de datas" });
      return;
    }
    if (toDate < fromDate || fromDate > new Date() || toDate > new Date()) {
      toast({ variant: "destructive", description: "Intervalo de datas inválido" });
      return;
    }

    if (!selectedCodes.length) {
      toast({ variant: "destructive", description: "Selecione ao menos uma loja" });
      return;
    }
    if (!codInvoiceModel) {
      toast({ variant: "destructive", description: "Selecione um modelo de nota (55/65)" });
      return;
    }
    if (!invoiceType) {
      toast({ variant: "destructive", description: "Slecione um tipo de nota (Entrada/Saída)" });
      return;
    }

    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!fromDate || !toDate || !codInvoiceModel || !invoiceType) return;

    const payload = {
      stores: selectedCodes,
      cnpjs: selectedCnpjs,
      invoiceType,
      initialDate: fromDate.toISOString().split("T")[0],
      finalDate: toDate.toISOString().split("T")[0],
      codInvoiceModel,
    } as const;

    try {
      const res = await createExtractSchedules(payload).unwrap();
      const count = res.jobs?.length ?? 0;
      toast({ description: count ? `Agendamento criado para ${count} loja(s).` : "Nenhum agendamento foi criado" });

      reset();
      setFormKey(k => k + 1);
    } catch (err: any) {
      const msg = err?.data?.message || err?.error || "Falha ao criar agendamento. Tente novamente.";
      toast({ variant: "destructive", description: msg });
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <PageLayout>
      <PageTitle title="Extração de Notas Fiscais Ofm" description="Extração em massa" />
      <ContentContainer>
        <form key={formKey} onSubmit={handleSubmit} className="w-full">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-grow"><SelectFilters /></div>
            <div className="flex-grow"><SelectCoupon /></div>
            <div className="flex-grow"><SelectEntryExit /></div>
            <div className="flex-grow"><StoreFilter /></div>
            <Button type="submit" className="h-8" disabled={isLoading}>
              {isLoading ? "Agendando..." : "Enviar agendamento"}
            </Button>
          </div>
        </form>
        <div id="ofm-scroll-root" className="max-h-[70vh] overflow-y-auto">
          <Schedulers />
        </div>

        <Dialog open={confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(false)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Recalcular / extrair OFM</DialogTitle>
              <DialogDescription>
                Será agendada a extração OFM de{" "}
                <span className="font-semibold text-foreground">
                  {selectedCodes.length} loja(s)
                </span>
                . Esse processo pode demorar alguns minutos para ser concluído.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={() => setConfirmOpen(false)}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button onClick={handleConfirm} disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirmar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </ContentContainer>
    </PageLayout>
  );
};
